/**
 * Endpoints de **Finanzas → Contabilidad** (`/finanzas/contabilidad`).
 *
 * Son cuatro llamadas cortas: dos descargas de Excel (el reporte de ventas con
 * formato CONCAR y los anexos de las facturas), la conversión de un Excel a DBF y
 * la descarga de cada DBF generado.
 */

import { apiClient } from '@/lib/http/api-client';

import type { DocumentoConcar, PeriodoConcar, ResultadoDbf } from './contabilidad.types';

/** Cadena de consulta del período y el documento. */
export function cadenaDeConcar(periodo: PeriodoConcar, documento?: DocumentoConcar): string {
  const parametros = new URLSearchParams({
    mes: String(periodo.mes),
    anio: String(periodo.anio),
  });

  if (documento) parametros.set('doc', documento);

  return parametros.toString();
}

export const contabilidadApi = {
  /** Excel de ventas del mes con el formato de importación de CONCAR. */
  urlConcar: (periodo: PeriodoConcar, documento: DocumentoConcar) =>
    `/api/finanzas/contabilidad/concar?${cadenaDeConcar(periodo, documento)}`,

  /** Excel con los anexos (RUC y razón social) de las facturas del mes. */
  urlAnexos: (periodo: PeriodoConcar) =>
    `/api/finanzas/contabilidad/anexos?${cadenaDeConcar(periodo)}`,

  /** Convierte la plantilla de Excel en los dos DBF de CONCAR. */
  convertir: (archivo: File) => {
    const formulario = new FormData();

    formulario.set('excel', archivo);

    return apiClient.post<ResultadoDbf>('/api/finanzas/contabilidad/dbf', {
      body: formulario,
    });
  },

  /** Descarga de un DBF ya generado. */
  urlDbf: (nombre: string) => `/api/finanzas/contabilidad/dbf/${encodeURIComponent(nombre)}`,
};

/** Los tres documentos que se pueden descargar del período. */
export function nombreDeConcar(periodo: PeriodoConcar, documento: DocumentoConcar): string {
  const mes = String(periodo.mes).padStart(2, '0');

  return `CONCAR_${documento}_${mes}-${periodo.anio}.xlsx`;
}

export function nombreDeAnexos(periodo: PeriodoConcar): string {
  const mes = String(periodo.mes).padStart(2, '0');

  return `DBF_FACTURA_${mes}-${periodo.anio}.xlsx`;
}
